/**
 * The outbox: every local write, durable, ordered and idempotent.
 *
 * A write is committed to Dexie and appended here in one transaction. If the app
 * is killed a millisecond later, the edit is still on disk and still queued -
 * which is the whole promise of "you can work offline".
 */
import {
  SYNC_PUSH_BATCH_SIZE,
  SYNC_PUSH_MAX_BYTES,
  type SyncMutation,
  type SyncMutationResult,
  type SyncTable,
} from '@mouldtrack/shared';
import { SYNC_TABLE_MAP, db, type ConflictEntry, type OutboxEntry } from '../db/schema.js';
import { newId } from '../lib/id.js';
import { patchStatus } from './status.js';

/** Give up automatic retries after this many failures; the user then decides. */
const MAX_ATTEMPTS = 8;

/** Exponential backoff, capped at five minutes. */
function backoffMs(attempts: number): number {
  return Math.min(1000 * 2 ** attempts, 5 * 60_000);
}

export interface EnqueueInput {
  table: SyncTable;
  op: SyncMutation['op'];
  id: string;
  data?: Record<string, unknown>;
  baseUpdatedAt?: string | null;
}

/**
 * Append a mutation. Call this *inside* the same Dexie transaction as the local
 * write - `mutations.ts` does, which is why an interrupted save can never leave
 * a changed row with no queued mutation (or the reverse).
 */
export async function enqueue(input: EnqueueInput): Promise<OutboxEntry> {
  const entry: OutboxEntry = {
    clientMutationId: newId(),
    table: input.table,
    op: input.op,
    id: input.id,
    data: input.data,
    baseUpdatedAt: input.baseUpdatedAt ?? null,
    clientTimestamp: new Date().toISOString(),
    status: 'queued',
    attempts: 0,
  };
  await db.outbox.add(entry);
  return entry;
}

/**
 * Mutations ready to send right now (backoff respected).
 *
 * A batch closes at a size as well as a count. A hundred moulds with a photo
 * each came to 11 MB; the proxy refused it, every retry, and nothing queued
 * behind it ever left the device - while the screen showed every one of them.
 * A single mutation larger than the budget still goes, alone, so it fails on
 * its own rather than holding up everything after it.
 */
export async function claimBatch(): Promise<OutboxEntry[]> {
  const now = new Date().toISOString();
  const rows = await db.outbox.where('status').anyOf('queued', 'sending').sortBy('seq');
  const batch: OutboxEntry[] = [];
  let bytes = 0;
  for (const r of rows) {
    if (r.nextAttemptAt && r.nextAttemptAt > now) continue;
    const size = JSON.stringify(r.data ?? null).length + 300;
    if (batch.length > 0 && bytes + size > SYNC_PUSH_MAX_BYTES) break;
    batch.push(r);
    bytes += size;
    if (batch.length >= SYNC_PUSH_BATCH_SIZE) break;
  }
  return batch;
}

/** Apply the server's verdict on a pushed batch. */
export async function settleBatch(
  batch: OutboxEntry[],
  results: SyncMutationResult[],
): Promise<{ conflicts: number; rejected: number }> {
  const byId = new Map(results.map((r) => [r.clientMutationId, r]));
  let conflicts = 0;
  let rejected = 0;

  for (const entry of batch) {
    const result = byId.get(entry.clientMutationId);

    // No verdict for this mutation - the server never saw it. Leave it queued.
    if (!result) continue;

    if (result.status === 'applied') {
      await db.transaction('rw', db.outbox, db.table(SYNC_TABLE_MAP[entry.table]), async () => {
        if (entry.seq !== undefined) await db.outbox.delete(entry.seq);
        if (result.row) {
          // Trust the server's version: it holds the authoritative assetCode
          // (which it may have allocated) and the canonical updatedAt.
          await db.table(SYNC_TABLE_MAP[entry.table]).put({ ...result.row, _pending: 0 });
        } else if (entry.op === 'delete') {
          await db.table(SYNC_TABLE_MAP[entry.table]).delete(entry.id);
        }
      });
      continue;
    }

    if (result.status === 'conflict') {
      conflicts += 1;
      const conflict: ConflictEntry = {
        table: entry.table,
        rowId: entry.id,
        detectedAt: new Date().toISOString(),
        localData: entry.data ?? {},
        serverData: (result.row ?? {}) as Record<string, unknown>,
        resolved: 0,
      };
      await db.transaction('rw', db.outbox, db.conflicts, db.table(SYNC_TABLE_MAP[entry.table]), async () => {
        await db.conflicts.add(conflict);
        if (entry.seq !== undefined) await db.outbox.delete(entry.seq);
        // The server's row becomes the visible truth; the losing edit is not
        // thrown away, it is parked in `conflicts` for the user to review.
        if (result.row) await db.table(SYNC_TABLE_MAP[entry.table]).put({ ...result.row, _pending: 0 });
      });
      continue;
    }

    // Rejected: validation or permission. Retrying cannot help.
    rejected += 1;
    if (entry.seq !== undefined) {
      await db.outbox.update(entry.seq, {
        status: 'failed',
        attempts: entry.attempts + 1,
        lastError: result.reason ?? result.code ?? 'Rejected by server',
      });
    }
  }

  return { conflicts, rejected };
}

/** The whole batch failed in transit - back off and try again later. */
export async function deferBatch(batch: OutboxEntry[], error: string): Promise<void> {
  for (const entry of batch) {
    if (entry.seq === undefined) continue;
    const attempts = entry.attempts + 1;
    await db.outbox.update(entry.seq, {
      attempts,
      status: attempts >= MAX_ATTEMPTS ? 'failed' : 'queued',
      lastError: error,
      nextAttemptAt: new Date(Date.now() + backoffMs(attempts)).toISOString(),
    });
  }
}

/**
 * Push the counters the top bar renders.
 *
 * Scans are counted alongside catalog edits even though they travel by a
 * different queue and a different endpoint, because every question these numbers
 * answer treats them the same. "3 changes will sync when you reconnect" is true
 * of a scan. So is "signing out discards them" - `clearReplicatedData` wipes both
 * queues, and a sign-out warning that said "0 changes" while quietly destroying a
 * morning's scans would be the worst kind of lie this app could tell.
 */
export async function refreshCounters(): Promise<void> {
  const [pendingEdits, failedEdits, conflicts, pendingScans, failedScans] = await Promise.all([
    db.outbox.where('status').anyOf('queued', 'sending').count(),
    db.outbox.where('status').equals('failed').count(),
    db.conflicts.where('resolved').equals(0).count(),
    db.scanQueue.where('status').anyOf('queued', 'sending').count(),
    db.scanQueue.where('status').equals('failed').count(),
  ]);
  patchStatus({
    pending: pendingEdits + pendingScans,
    failed: failedEdits + failedScans,
    conflicts,
  });
}

/** Discard a mutation the server will never accept, after the user says so. */
export async function discardFailed(seq: number): Promise<void> {
  const entry = await db.outbox.get(seq);
  if (!entry) return;
  await db.outbox.delete(seq);
  // Drop the optimistic local row too; the next pull restores the server's truth.
  if (entry.op === 'create') {
    await db.table(SYNC_TABLE_MAP[entry.table]).delete(entry.id);
  } else {
    await db.table(SYNC_TABLE_MAP[entry.table]).update(entry.id, { _pending: 0 });
  }
  await refreshCounters();
}

/** Re-arm a failed mutation for another attempt. */
export async function retryFailed(seq: number): Promise<void> {
  await db.outbox.update(seq, { status: 'queued', attempts: 0, nextAttemptAt: undefined });
  await refreshCounters();
}
